import { useEffect } from "react";

const env = import.meta.env;
const isDevelopment = env.DEV;

async function fetchBrazeToken() {
  const token = sessionStorage.getItem("token");
  const baseUrl = env.VITE_CONNECT_URL;

  const url = `${baseUrl}/V2/api/entry.aspx?method=get_braze_token&token=${token}`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`[Braze] Token fetch failed: ${response.status}`);
  }
  const data = await response.json();
  if (!data.isValid) {
    throw new Error(`[Braze] Token fetch error: ${data.errorCode}`);
  }
  const { jwt } = data.payLoad;
  // externalId is the `sub` claim inside the JWT payload — decode it here
  // so the server never returns the user ID as a plain field
  const sub = JSON.parse(atob(jwt.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
  return { jwt, externalId: sub.sub };
}

/**
 * Custom hook to initialize Braze SDK once on app mount with JWT authentication.
 * Credentials are loaded from environment variables.
 * The JWT and externalId are fetched from /api/braze-token (uses session cookie).
 */
export default function useBraze() {
  useEffect(() => {
    let subscriptionId;
    let brazeRef;

    async function initBraze() {
      try {
        const apiKey = env.VITE_BRAZE_API_KEY;
        const endpoint = env.VITE_BRAZE_ENDPOINT;

        if (!apiKey || !endpoint) {
          if (isDevelopment) {
            console.warn(
              "[Braze] Missing credentials. Set VITE_BRAZE_API_KEY and VITE_BRAZE_ENDPOINT in .env files."
            );
          }
          return;
        }

        const braze = await import("@braze/web-sdk");
        brazeRef = braze;

        braze.initialize(apiKey, {
          baseUrl: endpoint,
          enableLogging: isDevelopment,
          enableSdkAuthentication: true,
        });

        // currentUserId is set after the first successful changeUser call.
        // The failure callback checks it to avoid refreshing a JWT for a
        // stale or different user (prevents cross-user JWT reuse).
        let currentUserId = null;

        // Register JWT expiry handler BEFORE changeUser/openSession so no
        // requests go out without a valid signature.
        subscriptionId = braze.subscribeToSdkAuthenticationFailures(
          async ({ errorCode, reason, userId }) => {
            // Guard: only refresh for the currently logged-in user.
            // If userId doesn't match, the session has changed — do nothing.
            if (userId !== currentUserId) {
              if (isDevelopment) {
                console.warn(
                  "[Braze] Auth failure userId mismatch — skipping refresh",
                  { errorCode, reason }
                );
              }
              return;
            }
            if (isDevelopment) {
              console.warn("[Braze] SDK auth failure — refreshing JWT", {
                errorCode,
                reason,
                userId,
                // NOTE: never log error.signature — it contains sensitive credentials
              });
            }
            try {
              const { jwt } = await fetchBrazeToken();
              braze.setSdkAuthenticationSignature(jwt);
            } catch (err) {
              if (isDevelopment) {
                console.error("[Braze] Failed to refresh JWT:", err);
              }
            }
          }
        );

        const { jwt, externalId } = await fetchBrazeToken();
        currentUserId = externalId;

        braze.changeUser(externalId, jwt);

        braze.openSession();

        if (isDevelopment) {
          console.log("[Braze] SDK initialized with JWT auth", {
            endpoint,
            apiKey: apiKey.substring(0, 8) + "...",
            externalId,
          });
        }
      } catch (error) {
        if (isDevelopment) {
          console.error("[Braze] Failed to initialize SDK:", error);
        }
      }
    }

    initBraze();

    return () => {
      if (subscriptionId && brazeRef) {
        brazeRef.removeSubscription(subscriptionId);
      }
    };
  }, []);
}
